import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';
import useFetch, { CachePolicies, Res } from 'use-http';

import { identifyUser, removeRedirectURLLocalStorage } from '@/helpers';
import getDeviceId from '@/helpers/getDeviceId';
import { useSelector } from '@/redux/helper';
import { setSessionChecked, setUserData, unsetUserData } from '@/redux/slices/user/actions';
import { buildURL, patch, post } from '@/shared/fetch';
import { getClientId } from '@/shared/ga';
import { trackMP } from '@/shared/mp';

import { useViralLoops } from '../ReferralProvider/hooks';

export const useLogout = (onLogout?: () => void) => {
  const dispatch = useDispatch();
  const viralLoops = useViralLoops();

  const { mutate: logout } = useMutation({
    mutationFn: () => post(buildURL('/users/logout'), { deviceId: getDeviceId() }),
    onSuccess: () => {
      onLogout?.();
      localStorage.removeItem('giftModal');
      removeRedirectURLLocalStorage();
      viralLoops
        ?.getCampaign()
        .then((campaign) => campaign?.logout())
        .catch((error) => {
          console.warn('Failed to logout from viral loops campaign:', error);
        });
      dispatch(unsetUserData());
      route('/login');
    },
  });

  return useCallback(() => logout(), [logout]);
};

const updateUserGAClientIds = async (user: { gaClientIds?: string[]; _id: string }) => {
  const clientId = await getClientId();
  const userClientIds = user.gaClientIds ?? [];
  if (!userClientIds.includes(clientId)) {
    await patch(buildURL(`/users/${user._id}/clientId`), {
      gaClientIds: [...userClientIds, clientId],
    });
  }
};

export const useFetchUserIfNeeded = (onResponse?: (response: Res<any>) => void) => {
  const { get, response } = useFetch(buildURL('/users/user'), {
    credentials: 'include',
    cachePolicy: CachePolicies.NO_CACHE,
  });
  const dispatch = useDispatch();
  const sessionChecked = useSelector((state) => state.user.sessionChecked);

  const onResponseRef = useRef(onResponse);

  useEffect(() => {
    onResponseRef.current = onResponse;
  }, [onResponse]);

  useEffect(() => {
    if (sessionChecked) {
      return undefined;
    }

    const loadUserData = async () => {
      await get();

      if (response.status === 200) {
        const user = response.data;
        dispatch(setUserData(user));
        updateUserGAClientIds(user);
        identifyUser(user);
        trackMP('login', { method: 'automatic' });
      } else if (response.status === 204) {
        dispatch(unsetUserData());
      } else {
        dispatch(setSessionChecked());
      }

      onResponseRef.current?.(response);
    };

    const id = setTimeout(loadUserData, 500);
    return () => clearTimeout(id);
  }, [get, response, sessionChecked, dispatch]);
};
